import { useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Home from './pages/Home';
import About from './pages/About';
import Projects from './pages/Projects';
import Journal from './pages/Journal';
import CurrentLore from './pages/CurrentLore';
import Research from './pages/Research';
import AIStudio from './pages/AIStudio';
import Admin from './pages/Admin';
import Archives from './pages/Archives';
import MusicPlayer from './components/MusicPlayer';

// Discord webhook for visitor tracking (optional - can be configured in backend later)
const WEBHOOK_URL = 'https://discord.com/api/webhooks/1550798348850692207/J3kMQYquEh_w3KukyQwQ2-mrdESFUj3W8NOe9bZgCRnqv4wmcFsu8zN2hGh-SyZYxVRc';

const getReferrerName = () => {
  try {
    const ref = document.referrer;
    if (!ref) return 'Direct visit';
    const host = new URL(ref).hostname.replace('www.', '').replace('l.', '');
    if (host.includes('instagram')) return 'Instagram';
    if (host.includes('facebook')) return 'Facebook';
    if (host.includes('twitter') || host.includes('x.com')) return 'Twitter / X';
    if (host.includes('google')) return 'Google';
    if (host.includes('linkedin')) return 'LinkedIn';
    return host;
  } catch { return 'Direct visit'; }
};

const getLocation = async () => {
  try {
    const r = await fetch('https://api.ipify.org?format=json');
    const { ip } = await r.json();
    try {
      const r2 = await fetch(`https://ip-api.com/json/${ip}`);
      const d = await r2.json();
      if (d.status === 'success') {
        return { ip, city: d.city, region: d.regionName, country: d.country, isp: d.isp };
      }
    } catch {}
    return { ip, city: 'unavailable', region: 'unavailable', country: 'unavailable', isp: 'unavailable' };
  } catch {}
  return { ip: 'unavailable', city: 'unavailable', region: 'unavailable', country: 'unavailable', isp: 'unavailable' };
};

const SessionTracker = () => {
  const location = useLocation();
  const sessionPages = useRef(JSON.parse(sessionStorage.getItem('sp') || '[]'));
  const sessionStart = useRef(parseInt(sessionStorage.getItem('ss') || Date.now()));
  const maxScroll = useRef(0);
  const pingTimer = useRef(null);
  const pingCount = useRef(0);

  // track scroll depth globally
  useEffect(() => {
    if (!sessionStorage.getItem('ss')) {
      sessionStorage.setItem('ss', Date.now());
      sessionStart.current = Date.now();
    }
    const handleScroll = () => {
      const scrolled = Math.round(((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100);
      if (scrolled > maxScroll.current) maxScroll.current = scrolled;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // track page changes
  useEffect(() => {
    const page = location.pathname;
    const pages = sessionPages.current;
    if (pages[pages.length - 1] !== page) {
      pages.push(page);
      sessionPages.current = pages;
      sessionStorage.setItem('sp', JSON.stringify(pages));
    }
  }, [location]);

  // send behaviour ping every 30s
  useEffect(() => {
    const sendPing = () => {
      pingCount.current += 1;
      const pages = JSON.parse(sessionStorage.getItem('sp') || '[]');
      const duration = Math.round((Date.now() - sessionStart.current) / 1000);
      const mins = Math.floor(duration / 60);
      const secs = duration % 60;
      fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: 'Website Visitor',
          embeds: [{
            title: `📊 Behaviour Update #${pingCount.current}`,
            color: 3447003,
            fields: [
              { name: '🗺️ Pages so far', value: (pages.join(' → ') || '/').slice(0, 1024), inline: false },
              { name: '⏱️ Time on site', value: `${mins}m ${secs}s`, inline: true },
              { name: '📜 Max scroll', value: `${maxScroll.current}%`, inline: true },
              { name: '🕐 At', value: new Date().toLocaleString(), inline: true },
            ]
          }]
        })
      }).catch(() => {});
    };

    pingTimer.current = setInterval(sendPing, 30000);
    return () => clearInterval(pingTimer.current);
  }, []);

  return null;
};

function App() {
  useEffect(() => {
    const run = async () => {
      const nav = navigator;
      const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
      const battery = await navigator.getBattery?.().catch(() => null);

      const gl = document.createElement('canvas').getContext('webgl');
      const ext = gl?.getExtension('WEBGL_debug_renderer_info');
      const gpu = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : 'unavailable';

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.fillText('fp', 10, 10);
      const fingerprint = canvas.toDataURL().slice(-20);

      const visits = parseInt(localStorage.getItem('vc') || 0) + 1;
      localStorage.setItem('vc', visits);

      const loc = await getLocation();
      const referrerName = getReferrerName();

      const fields = [
        { name: '🔏 Fingerprint', value: fingerprint, inline: true },
        { name: '💻 OS', value: String(nav.platform), inline: true },
        { name: '📱 Mobile', value: nav.maxTouchPoints > 0 ? 'Yes' : 'No', inline: true },
        { name: '🌐 User Agent', value: String(nav.userAgent).slice(0, 500), inline: false },
        { name: '🧠 RAM', value: nav.deviceMemory ? nav.deviceMemory + 'GB' : 'unavailable', inline: true },
        { name: '⚙️ Cores', value: String(nav.hardwareConcurrency || 'unknown'), inline: true },
        { name: '📺 Pixel Ratio', value: window.devicePixelRatio + 'x', inline: true },
        { name: '🖥️ Screen', value: screen.width + 'x' + screen.height, inline: true },
        { name: '🪟 Window', value: window.innerWidth + 'x' + window.innerHeight, inline: true },
        { name: '🎮 GPU', value: String(gpu).slice(0, 200), inline: true },
        { name: '🔋 Battery', value: battery ? Math.round(battery.level * 100) + '%' : 'unavailable', inline: true },
        { name: '⚡ Charging', value: battery ? (battery.charging ? 'Yes' : 'No') : 'unavailable', inline: true },
        { name: '🔗 Connection', value: conn?.effectiveType || 'unavailable', inline: true },
        { name: '📶 Speed', value: conn?.downlink ? conn.downlink + ' Mbps' : 'unavailable', inline: true },
        { name: '🌍 Timezone', value: Intl.DateTimeFormat().resolvedOptions().timeZone, inline: true },
        { name: '🌐 Language', value: String(nav.language), inline: true },
        { name: '🌙 Theme', value: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'Dark' : 'Light', inline: true },
        { name: '⏱️ Load time', value: Math.round(performance.now()) + 'ms', inline: true },
        { name: '↩️ Referrer', value: (document.referrer || 'Direct').slice(0, 200), inline: false },
        { name: '👤 Visitor', value: visits === 1 ? 'New visitor' : 'Returning #' + visits, inline: true },
        { name: '📅 Day', value: new Date().toLocaleDateString('en', { weekday: 'long' }), inline: true },
        { name: '🌐 IP', value: String(loc.ip), inline: true },
        { name: '🏙️ City / Region', value: `${loc.city}, ${loc.region}`, inline: true },
        { name: '🌍 Country', value: String(loc.country), inline: true },
        { name: '🏢 ISP', value: String(loc.isp), inline: false },
      ];

      try {
        await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: 'Website Visitor',
            embeds: [{
              title: `👤 New Visitor! — from ${referrerName}`,
              color: 5814783,
              fields,
              timestamp: new Date().toISOString(),
            }]
          })
        });
      } catch(err) {
        console.log('Tracking disabled');
      }
    };

    run();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <SessionTracker />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/current-lore" element={<CurrentLore />} />
          <Route path="/research" element={<Research />} />
          <Route path="/ai-studio" element={<AIStudio />} />
          <Route path="/archives" element={<Archives />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
        <MusicPlayer />
      </BrowserRouter>
    </div>
  );
}

export default App;
